<template>
  <b-container fluid>
    <b-row class="mb-3">
      <b-col cols="6" sm="3" md="2" class="mt-5">
        <label class="field-label mb-1">Year</label>
        <select v-model.number="selectedYear" class="form-control">
          <option v-for="year in availableYears" :key="year" :value="year">
            {{ year }}
          </option>
        </select>
      </b-col>
      <b-col cols="6" sm="3" md="2" class="mt-5">
        <label class="field-label mb-1">Month</label>
        <select v-model.number="selectedMonth" class="form-control">
          <option :value="0">Whole year</option>
          <option v-for="(name, index) in monthNames" :key="name" :value="index + 1">
            {{ name }}
          </option>
        </select>
      </b-col>
      <b-col class="mt-5" v-if="canFilterTeam10">
        <b-form-checkbox
          id="checkbox-exclude-team10"
          class="mt-4"
          v-model="excludeTeam10"
          name="checkbox-exclude-team10"
        >
          Exclude team10vaghelait@gmail.com events
        </b-form-checkbox>
      </b-col>
    </b-row>

    <!-- Monthly totals for the selected year -->
    <b-row>
      <b-col>
        <h5 class="mb-2">{{ selectedYear }} — Monthly Totals</h5>
        <b-table striped hover :items="monthlyBreakdown" :fields="monthlyFields"></b-table>
      </b-col>
    </b-row>

    <!-- Breakdown for the selected period (year or month) -->
    <b-row>
      <b-col>
        <h5 class="mb-2">{{ periodLabel }} — Breakdown by API Call</h5>
        <b-table striped hover :items="periodBreakdown"></b-table>
      </b-col>
    </b-row>

    <!-- Detailed event log for the selected period -->
    <b-row>
      <b-col>
        <h5 class="mb-2">{{ periodLabel }} — Event Log</h5>
        <b-table
          :items="periodCalls"
          :fields="fields"
          stacked="md"
          show-empty
          small
        >
          <template #[`cell(event)`]="row">
            {{ eventLabel(row.item.event) }}
          </template>
          <template #[`cell(timestamp)`]="row">
            {{ formatDate(row.item.timestamp) }}
          </template>
          <template #[`cell(cost)`]="row">
            £{{ Number(row.item.cost || 0).toFixed(2) }}
          </template>
        </b-table>
      </b-col>
    </b-row>
  </b-container>
</template>

<script>
  import moment from 'moment'
  import { mapState, mapGetters, mapActions } from 'vuex'

  // Cost per API call, in GBP.
  const EVENT_COSTS = {
    regLookup: 0.85,
    valuationTool: 0.25,
    addStock: 0,
  }

  const EVENT_LABELS = {
    regLookup: 'Reg Lookup',
    addStock: 'Advert Created',
    valuationTool: 'Valuation Tool',
  }

  const TEAM10_EMAIL = 'team10vaghelait@gmail.com'

  export default {
    name: 'APIUsage',
    data() {
      return {
        fields: [
          {
            key: 'event',
            label: 'API Call',
            sortable: true,
            class: 'text-center',
          },
          { key: 'timestamp', label: 'Date/Time', sortable: true },
          { key: 'user', label: 'User', sortable: true },
          { key: 'vehicleReg', label: 'VehicleReg', sortable: true },
          { key: 'cost', label: 'Cost', sortable: true },
        ],
        monthlyFields: [
          { key: 'month', label: 'Month' },
          { key: 'reg_lookups', label: 'Reg Lookups' },
          { key: 'adverts_created', label: 'Adverts Created' },
          { key: 'valuations', label: 'Valuations' },
          { key: 'total_cost', label: 'Total Cost' },
        ],
        monthNames: moment.months(),
        selectedYear: moment().year(),
        // 0 = whole year, 1-12 = specific month
        selectedMonth: 0,
        excludeTeam10: true,
      }
    },
    computed: {
      ...mapGetters('admin', ['getAllAPIUsageCalls']),
      ...mapState('user', ['user']),
      // Only the team10 user ever sees their own events in the data set, so
      // the exclude toggle is only meaningful (and shown) to them.
      canFilterTeam10() {
        return this.user && this.user.email === TEAM10_EMAIL
      },
      // All years present in the data, plus 2021..current, newest first.
      availableYears() {
        const years = new Set()
        for (let y = 2021; y <= moment().year(); y++) years.add(y)
        this.getAllAPIUsageCalls.forEach((call) => {
          const y = this.callYear(call)
          if (y) years.add(y)
        })
        return Array.from(years).sort((a, b) => b - a)
      },
      // Calls for the selected year, after applying the team10 filter.
      callsForYear() {
        return this.getAllAPIUsageCalls.filter((call) => {
          if (this.excludeTeam10 && call.user === TEAM10_EMAIL) return false
          return this.callYear(call) === this.selectedYear
        })
      },
      // Calls for the selected period (year, or a specific month within it).
      periodCalls() {
        if (!this.selectedMonth) return this.callsForYear
        return this.callsForYear.filter((call) => {
          return this.callMonth(call) === this.selectedMonth
        })
      },
      periodLabel() {
        if (!this.selectedMonth) return `${this.selectedYear}`
        return `${this.monthNames[this.selectedMonth - 1]} ${this.selectedYear}`
      },
      // Per-event-type breakdown for the selected period.
      periodBreakdown() {
        return this.buildBreakdown(this.periodCalls)
      },
      // One row per month of the selected year, with a totals row.
      monthlyBreakdown() {
        const rows = this.monthNames.map((name, index) => {
          const monthCalls = this.callsForYear.filter((call) => {
            return this.callMonth(call) === index + 1
          })
          return this.summariseMonth(name, monthCalls)
        })
        rows.push(this.summariseMonth('Total', this.callsForYear))
        return rows
      },
    },
    mounted() {
      this.dbGetAPIUsage()
    },
    methods: {
      ...mapActions('admin', ['dbGetAPIUsage']),
      callYear(call) {
        if (!call.timestamp || !call.timestamp.seconds) return null
        return moment.unix(call.timestamp.seconds).year()
      },
      callMonth(call) {
        if (!call.timestamp || !call.timestamp.seconds) return null
        return moment.unix(call.timestamp.seconds).month() + 1
      },
      countEvent(calls, event) {
        return calls.filter((call) => call.event === event).length
      },
      summariseMonth(label, calls) {
        const regLookups = this.countEvent(calls, 'regLookup')
        const advertsCreated = this.countEvent(calls, 'addStock')
        const valuations = this.countEvent(calls, 'valuationTool')
        const total =
          regLookups * EVENT_COSTS.regLookup +
          advertsCreated * EVENT_COSTS.addStock +
          valuations * EVENT_COSTS.valuationTool
        return {
          month: label,
          reg_lookups: regLookups,
          adverts_created: advertsCreated,
          valuations: valuations,
          total_cost: `£${total.toFixed(2)}`,
        }
      },
      buildBreakdown(calls) {
        return Object.keys(EVENT_LABELS).map((event) => {
          const number = this.countEvent(calls, event)
          return {
            api_call: EVENT_LABELS[event],
            number: `${number}`,
            total_cost: `£${(number * EVENT_COSTS[event]).toFixed(2)}`,
          }
        })
      },
      eventLabel(event) {
        return EVENT_LABELS[event] || event
      },
      formatDate(value) {
        if (value && value.seconds) {
          return moment.unix(value.seconds).format('DD/MM/YYYY HH:mm')
        }
      },
    },
  }
</script>

<style scoped>
  .field-label {
    font-size: 10pt;
  }
</style>
